import {
  ageBandUpperEdge,
  snapToBandFloor,
} from "@/lib/age-bands";
import { useRef, useState } from "react";
import {
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
  type LayoutChangeEvent,
} from "react-native";
import { useTranslation } from "react-i18next";

import {
  AGE_RANGE_MIN,
  AGE_RANGE_SLIDER_MAX,
  ANY_AGE_RANGE_LABEL,
  type NumericAgeRange,
  clampAge,
  formatAgeRange,
  parseAgeRangeForSlider,
} from "@/lib/age-range";

/**
 * The age-range filter control — one track with two thumbs, inline in the
 * sheet, in the same visual shape as `NearbyRadiusSlider` (current choice
 * announced above the track, "18" and "75+" anchors below). It replaced a
 * dropdown that opened a popup for the same track.
 *
 * The platform slider draws a single thumb, so this track is drawn by hand:
 * a touch grabs whichever thumb is nearer, dragging moves it in one-year
 * steps, and neither thumb can pass the other. The full 18–75+ span reads as
 * "Any age" and stores `null`; 75 is open-ended ("50+") — the same label
 * strings filter state and `group.ageRange` carried before the rewrite.
 *
 * The slider owns the range while a finger is down; the prop wins otherwise
 * (same rule as `NearbyRadiusSlider` — a parent re-render mid-drag must not
 * snap a thumb back). Commits once, on release.
 */
export function AgeRangeSlider({
  selected,
  onChange,
  colors,
}: {
  selected: string | null;
  /**
   * Return the save's promise and the thumb holds the released position until
   * it settles. A caller with synchronous state can return nothing — its prop
   * lands in the same commit, so there is nothing to bridge.
   */
  onChange: (value: string | null) => void | Promise<void> | Promise<boolean>;
  colors: {
    track: string;
    trackRemaining: string;
    thumb: string;
    label: string;
    labelMuted: string;
  };
}) {
  const { t } = useTranslation();
  const [trackWidth, setTrackWidth] = useState(1);
  const [draft, setDraft] = useState<NumericAgeRange | null>(null);
  // Refs mirror the drag state because responder callbacks can fire between
  // renders — release must commit the value under the finger, not the value
  // of the render that attached the handler.
  const draftRef = useRef<NumericAgeRange | null>(null);
  const activeHandleRef = useRef<"min" | "max" | null>(null);
  // A live finger owns the thumbs: a commit that settles mid-drag must not
  // release the hold out from under it. Same rule as `NearbyRadiusSlider`.
  const commitToken = useRef(0);

  const range = draft ?? parseAgeRangeForSlider(selected);
  const label = formatAgeRange(range);
  const shownLabel =
    label === ANY_AGE_RANGE_LABEL ? t("home.filters.anyAge") : label;

  const span = AGE_RANGE_SLIDER_MAX - AGE_RANGE_MIN;
  // The thumbs' centers travel an inset rail, [THUMB/2, width - THUMB/2], so
  // a thumb resting on 18 or 75+ is a whole circle inside the container —
  // centering them on the raw track ends left half the circle hanging
  // outside, where ancestors clip it. This is the same inset the platform
  // slider's web build gets from laying the thumb between its track halves.
  const usable = Math.max(trackWidth - THUMB_SIZE, 1);
  const minX = (usable * (range.min - AGE_RANGE_MIN)) / span;
  const maxX = (usable * (range.max - AGE_RANGE_MIN)) / span;

  function ageFromEvent(event: GestureResponderEvent): number {
    const along = event.nativeEvent.locationX - THUMB_SIZE / 2;
    return clampAge(AGE_RANGE_MIN + (along / usable) * span);
  }

  /**
   * Move a thumb, snapped to a band edge.
   *
   * The track used to move in one-year steps, which let someone ask for 26-28 —
   * a precision the data does not have and the privacy model will not allow.
   * Filtering finer than we disclose leaks the finer value: a 25-25 request
   * identifies an exact age, and two overlapping windows differenced give the
   * same answer at any width. The server refuses a non-band range outright, so
   * an unsnapped thumb would not merely be imprecise — it would resolve to "any
   * age" without saying so.
   *
   * The MIN thumb carries a floor and the MAX thumb carries that band's UPPER
   * EDGE, which is why they snap differently. A max thumb resting on floor 30
   * means "up to 34", and rendering it as 30 while admitting 34-year-olds is
   * the display/filter disagreement bands exist to remove.
   */
  function applyHandle(handle: "min" | "max", age: number): NumericAgeRange {
    const current = draftRef.current ?? range;
    const floor = snapToBandFloor(age);
    if (handle === "min") {
      return { min: Math.min(floor, current.max), max: current.max };
    }
    const edge = ageBandUpperEdge(floor) ?? AGE_RANGE_SLIDER_MAX;
    return { min: current.min, max: Math.max(edge, current.min) };
  }

  function setDraftBoth(next: NumericAgeRange) {
    draftRef.current = next;
    setDraft(next);
  }

  function onTrackLayout(event: LayoutChangeEvent) {
    setTrackWidth(Math.max(1, event.nativeEvent.layout.width));
  }

  function onResponderGrant(event: GestureResponderEvent) {
    // Invalidates any commit still in flight, so its `finally` cannot clear
    // the draft this drag is about to own.
    commitToken.current += 1;
    const age = ageFromEvent(event);
    const handle =
      Math.abs(age - range.min) <= Math.abs(age - range.max) ? "min" : "max";
    activeHandleRef.current = handle;
    setDraftBoth(applyHandle(handle, age));
  }

  function onResponderMove(event: GestureResponderEvent) {
    const handle = activeHandleRef.current;
    if (!handle) return;
    setDraftBoth(applyHandle(handle, ageFromEvent(event)));
  }

  function onResponderRelease() {
    const next = draftRef.current;
    activeHandleRef.current = null;

    if (!next) {
      draftRef.current = null;
      setDraft(null);
      return;
    }

    /**
     * The draft is deliberately NOT cleared here.
     *
     * It is the position the thumb was released at, and dropping it now
     * repaints from the prop — which is still the PRE-DRAG value until the
     * parent's write round-trips. Where the parent holds the value in local
     * state that lands in the same commit (edit-profile, the group filter)
     * nothing is visible. Where it does not, the thumb visibly jumps back and
     * crawls to where it was dropped: the people filter has no local state at
     * all, deriving `peopleFilters` from `currentUser.peopleFilters`, so the
     * prop cannot move until an API write AND a nearby refresh have completed.
     */
    const token = ++commitToken.current;
    const nextLabel = formatAgeRange(next);

    void Promise.resolve(
      onChange(nextLabel === ANY_AGE_RANGE_LABEL ? null : nextLabel),
    )
      // Errors are the caller's to report; here only the hold matters. On
      // failure the prop never moves, so this release snaps the thumb back to
      // the real value — honestly, next to the caller's error notice.
      .catch(() => {})
      .finally(() => {
        if (commitToken.current !== token) return;
        draftRef.current = null;
        setDraft(null);
      });
  }

  return (
    <View>
      <Text style={[styles.value, { color: colors.label }]}>{shownLabel}</Text>
      <View
        onLayout={onTrackLayout}
        onStartShouldSetResponder={() => true}
        onMoveShouldSetResponder={() => true}
        onResponderGrant={onResponderGrant}
        onResponderMove={onResponderMove}
        onResponderRelease={onResponderRelease}
        onResponderTerminate={onResponderRelease}
        accessibilityLabel={t("home.filters.ageRange")}
        accessibilityValue={{
          min: AGE_RANGE_MIN,
          max: AGE_RANGE_SLIDER_MAX,
          text: shownLabel,
        }}
        style={styles.trackWrap}
      >
        <View
          style={[styles.track, { backgroundColor: colors.trackRemaining }]}
        />
        <View
          style={[
            styles.trackActive,
            {
              backgroundColor: colors.track,
              left: minX + THUMB_SIZE / 2,
              width: Math.max(0, maxX - minX),
            },
          ]}
        />
        <View
          pointerEvents="none"
          style={[styles.thumb, { backgroundColor: colors.thumb, left: minX }]}
        />
        <View
          pointerEvents="none"
          style={[styles.thumb, { backgroundColor: colors.thumb, left: maxX }]}
        />
      </View>
      <View style={styles.anchors}>
        <Text style={[styles.anchor, { color: colors.labelMuted }]}>
          {AGE_RANGE_MIN}
        </Text>
        <Text
          style={[styles.anchor, styles.anchorEnd, { color: colors.labelMuted }]}
        >
          {AGE_RANGE_SLIDER_MAX}+
        </Text>
      </View>
    </View>
  );
}

// Match the platform slider the other controls use, so the thumbs read as
// the same family.
const THUMB_SIZE = 20;

const styles = StyleSheet.create({
  value: { fontSize: 14, fontWeight: "600", marginBottom: 4 },
  trackWrap: { height: 28, justifyContent: "center" },
  track: { height: 4, borderRadius: 999 },
  trackActive: { position: "absolute", height: 4, borderRadius: 999 },
  thumb: {
    position: "absolute",
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: 999,
    elevation: 2,
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
  },
  anchors: { flexDirection: "row", marginTop: 2 },
  anchor: { flex: 1, fontSize: 11 },
  anchorEnd: { textAlign: "right" },
});
